import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

type Post = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  cover_image_url: string | null;
  published_at: string | null;
};

export default function Updates() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setError(null);
      setLoading(true);
      const { data, error } = await supabase
        .from('posts')
        .select('id, slug, title, excerpt, cover_image_url, published_at')
        .eq('is_published', true)
        .order('published_at', { ascending: false });

      if (!isMounted) return;
      if (error) {
        console.error('Failed to load posts', error);
        setError('Unable to load updates right now.');
      } else if (data) {
        setPosts(data as Post[]);
      }
      setLoading(false);
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="container" style={{ padding: '1.25rem 1rem 2rem 1rem' }}>
      <header style={{ marginBottom: '1.25rem' }}>
        <h1 style={{ fontSize: '1.75rem', marginBottom: '0.35rem' }}>Updates</h1>
        <p style={{ color: '#999', margin: 0 }}>Release notes and announcements.</p>
      </header>

      {loading && (
        <div className="card" style={{ padding: '1.5rem', textAlign: 'center' }}>
          <p style={{ margin: 0 }}>Loading updates…</p>
        </div>
      )}

      {error && !loading && (
        <div className="card" style={{ padding: '1.5rem', border: '1px solid var(--color-danger)' }}>
          <p style={{ color: 'var(--color-danger)', margin: 0 }}>{error}</p>
        </div>
      )}

      {!loading && !error && posts.length === 0 && (
        <div className="card" style={{ padding: '1.5rem', textAlign: 'center' }}>
          <p style={{ margin: 0, color: '#ccc' }}>No updates published yet.</p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {posts.map((post) => (
          <Link
            to={`/updates/${post.slug}`}
            key={post.id}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            <div
              className="card"
              style={{
                border: '1px solid #1f1f1f',
                padding: '1rem',
                display: 'flex',
                gap: '1rem',
                alignItems: 'center',
              }}
            >
              {post.cover_image_url ? (
                <img
                  src={post.cover_image_url}
                  alt=""
                  style={{
                    width: '72px',
                    height: '72px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    flexShrink: 0,
                  }}
                  loading="lazy"
                />
              ) : (
                <div
                  style={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #1a1a1a, #121212)',
                    border: '1px solid #222',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#777',
                    fontSize: '0.8rem',
                    flexShrink: 0,
                  }}
                >
                  Ember
                </div>
              )}

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                  <h2
                    style={{
                      fontSize: '1.05rem',
                      margin: 0,
                      color: '#f5f5f5',
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                    }}
                  >
                    {post.title}
                  </h2>
                  <span style={{ fontSize: '0.8rem', color: '#999', flexShrink: 0 }}>
                    {post.published_at
                      ? new Date(post.published_at).toLocaleDateString()
                      : 'Draft'}
                  </span>
                </div>
                {post.excerpt && (
                  <p
                    style={{
                      margin: '0.35rem 0 0',
                      color: '#cfcfcf',
                      fontSize: '0.9rem',
                      lineHeight: 1.45,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {post.excerpt}
                  </p>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
